import React, { useEffect, useRef, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';
import { Column } from 'react-table';

import {
  ArrowRightAltRounded,
  CheckRounded,
  Link as IconLink,
  InfoOutlined,
} from '@mui/icons-material';
import { Button, Grid, IconButton, Snackbar } from '@mui/material';

import Alert from '../../components/Alert';
import BackToDashboard from '../../components/BackToDashboard';
import CancelOrder from '../../components/CancelOrder';
import BasicConfirm from '../../components/Dialogs/BasicConfirm';
import DocumentsDownloader from '../../components/DocumentsDownloader';
import OrderAdminDetails from '../../components/OrderAdminDetails';
import OrderDetailsSection from '../../components/OrderDetailsSection';
import OrderDetailsTable from '../../components/Tables/OrderDetailsTable';
import Loading from '../../components/loading';
import { OrderStatus } from '../../constants/OrderStatus';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderTypes } from '../../constants/orderTypeConstants';
import { UserRoles } from '../../constants/roles';
import useExtractTagDocs from '../../hooks/useExtractTagDocs';
import useOrderActions from '../../hooks/useOrderActions';
import useOrderReloader from '../../hooks/useOrderReloader';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { closeSnackbar } from '../../redux-store/notifications/notificationsSlice';
import {
  cancelOrderAsync,
  setAlert,
} from '../../redux-store/orders/ordersSlice';
import { RootState } from '../../redux-store/store';
import { settlementTeamColumns } from '../../utils/OrderDetailsColumns';
import {
  getWindowDimensions,
  isOrderInactive,
  phoneObjToString,
} from '../../utils/helpers';

export default function OrderDetailsPage() {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cancelStyle = 'cancel-style';

  // Component States
  const [progressTimer, setProgressTimer] = useState<any>(undefined);
  const [showCancelConfirmation, setShowCancelConfirmation] =
    useState<boolean>(false);
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // Redux States
  const order: any = useAppSelector((state: RootState) => state.orders.order);
  const orders: any = useAppSelector((state: RootState) => state.orders);
  const isFetching: any = useAppSelector(
    (state: RootState) => state.orders.isFetching
  );
  const isFetchingText: any = useAppSelector(
    (state: RootState) => state.orders.isFetchingText
  );
  const alertUser: any = useAppSelector(
    (state: RootState) => state.orders.alertUser
  );
  const account: any = useAppSelector((state: RootState) => state.auth);
  const isThirdPartyOrder = order?.OrderType?.DocGen === DocGenerationSource.CX;
  const roles = JSON.parse(account?.currentUser?.['user_roles']);
  const isGlobal =
    account?.currentUser?.user_roles &&
    Object.keys(roles)?.includes(UserRoles.GLOBAL_USERS_ROLE);
  const isCurrentUserEscrowOnOrder =
    order?.OrderType?.DocGen === DocGenerationSource.CX
      ? order?.OrderDetails?.SettlementTeam[0]?.Emails.some(
          (email: string) =>
            email.toLowerCase() === account?.currentUser?.email?.toLowerCase()
        )
      : order?.OrderDetails?.EscrowTeam[0]?.Emails.some(
          (email: string) =>
            email.toLowerCase() === account?.currentUser?.email?.toLowerCase()
        );
  const [saToken, setSaToken] = useState<string>('');

  useOrderReloader(order);
  const { tagDocsLink } = useExtractTagDocs(order);
  const { getToken } = useOrderActions(order);
  const accordionPadding = getWindowDimensions().width > 900 ? '0' : '20px';

  useEffect(() => {
    const fetchSAAccessToken = (link: string, id: string) => {
      getToken(link, id).then((response) => {
        if (response && response.payload.length > 0) {
          setSaToken(response.payload);
        }
      });
    };
    if ((order?.Id, tagDocsLink.length !== 0 && saToken.length === 0)) {
      fetchSAAccessToken(tagDocsLink, order.Id);
    }
  }, [tagDocsLink]);

  useEffect(() => {
    return () => {
      dispatch(setAlert(null));
      dispatch(closeSnackbar());
    };
  }, [dispatch]);

  const goCancelOrder = () => {
    clearInterval(Number(progressTimer));
    setProgressTimer(undefined);
    const { id } = params;
    if (id) {
      dispatch(cancelOrderAsync({ orderId: id }));
    }
  };

  const handleCancelConfirmActions = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    action?: string
  ) => {
    setShowCancelConfirmation(false);
    if (action === 'primary') {
      goCancelOrder();
    }
  };

  const openPartyLink = () => {
    if (tagDocsLink) {
      window.open('', 'thisWindow');
      form.current?.submit();
    }
  };

  const escrowLenderColumns: Array<Column> = [
    {
      id: 'FirstName',
      Header: 'First',
      accessor: (row: any) => row.FirstName,
    },
    {
      id: 'LastName',
      Header: 'Last',
      accessor: (row: any) => row.LastName,
    },
    {
      id: 'Suffix',
      Header: 'Suffix',
      accessor: (row: any) => row.Suffix,
    },
    {
      id: 'Type',
      Header: 'Type',
      accessor: (row: any) => row.Type,
    },
    {
      id: 'Emails',
      Header: 'Email',
      accessor: (row: any) =>
        row.Emails && row.Emails.length > 0 ? row.Emails[0] : '',
    },
  ];

  const signerColumns: Array<Column> = [
    {
      id: 'FirstName',
      Header: 'First',
      accessor: (row: any) => row.FirstName,
    },
    {
      id: 'MiddleName',
      Header: 'Middle',
      accessor: (row: any) => row.MiddleName,
    },
    {
      id: 'LastName',
      Header: 'Last',
      accessor: (row: any) => row.LastName,
    },
    {
      id: 'Suffix',
      Header: 'Suffix',
      accessor: (row: any) => row.Suffix,
    },
    {
      id: 'PhoneNumbers',
      Header: 'Primary Phone',
      accessor: (row: any) => {
        row.PhoneNumbers && row.PhoneNumbers.length > 0
          ? phoneObjToString(row.PhoneNumbers[0])
          : '';
      },
      Cell: (data: any) =>
        data.row.original.PhoneNumbers &&
        data.row.original.PhoneNumbers.length > 0 ? (
          <>{phoneObjToString(data.row.original.PhoneNumbers[0])}</>
        ) : (
          <></>
        ),
    },
    {
      id: 'Emails',
      Header: 'Email',
      accessor: (row: any) =>
        row.Emails && row.Emails.length > 0 ? row.Emails[0] : '',
    },
  ];

  const hybridColumn = (testId: string) => {
    return {
      id: 'Link',
      Header: 'Link',
      accessor: (row: any) =>
        row.Emails && row.Emails.length > 0 ? row.Emails[0] : '',
      Cell: (data: any) => (
        <IconButton
          data-testid={testId}
          size='small'
          disabled={
            !tagDocsLink ||
            account.currentUser?.email?.toLowerCase() !==
              data?.row.original.Emails[0].toLowerCase()
          }
          onClick={() => openPartyLink()}>
          <IconLink />
        </IconButton>
      ),
    };
  };

  const isCancelOrderButtonVisible = () => {
    if (
      (isCurrentUserEscrowOnOrder || isGlobal) &&
      !isThirdPartyOrder &&
      order?.Cancelable &&
      !(
        order?.Status === OrderStatus.OpenAllSigned ||
        order?.Status?.includes(OrderStatus.Canceled)
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  if (order?.OrderType?.Type === OrderTypes.RON) {
    escrowLenderColumns.push(hybridColumn('escrow-team-link'));
    if (isThirdPartyOrder) {
      settlementTeamColumns.push(hybridColumn('settlement-team-link'));
    }
  }

  if (orders.isLoading) {
    return (
      <div className='full-page-loader'>
        <Loading
          text={
            orders.isLoading
              ? 'Loading Order Details...'
              : 'Sending registration email...'
          }
        />
      </div>
    );
  }

  return (
    <div>
      {isFetching && (
        <div className='fetching-spinner'>
          <Loading text={isFetchingText ? isFetchingText : 'Loading'} />
        </div>
      )}
      {/* This is for the Upload and Tag Title Documents section */}
      <form action={tagDocsLink} ref={form} method='POST' target='thisWindow'>
        <input ref={input} type='hidden' name='access_token' value={saToken} />
      </form>
      {/* Confirm order cancellation */}
      <BasicConfirm
        open={showCancelConfirmation}
        title='Confirm order cancelation'
        showTitle
        content='Are you sure you want to cancel this order? Canceled orders cannot be reopened.'
        primaryAction={handleCancelConfirmActions}
        primaryActionText='Confirm Order Cancelation'
        secondaryAction={handleCancelConfirmActions}
        secondaryActionText='Do not Cancel'
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertUser !== null}
        autoHideDuration={6000}
        onClose={() => dispatch(setAlert(null))}>
        <div>
          <Alert
            text={alertUser?.text}
            type={alertUser?.type}
            icon={
              alertUser?.type === 'error' ? <InfoOutlined /> : <CheckRounded />
            }
            elevated
            bold
          />
        </div>
      </Snackbar>
      {!orders.isLoading && (
        <Grid container>
          <OrderAdminDetails data={{ ...order, id: params.id }} hideSigners />
          <Grid container justifyContent='center' sx={{ paddingTop: 2 }}>
            {order?.Id?.slice(0, 1) === 'M' &&
              order?.Status === OrderStatus.OpenPending && (
                <Grid item xs={12} sm={12} md={12}>
                  <Grid container spacing={2} justifyContent='space-between'>
                    <h3>
                      This order is &ldquo;Pending&rdquo; full completion and
                      has not been submitted.
                    </h3>
                    <Button
                      component={Link}
                      to='/manual-order'
                      endIcon={<ArrowRightAltRounded />}
                      size='small'>
                      Edit Order
                    </Button>
                  </Grid>
                </Grid>
              )}
            <Grid item xs={5} md={3}>
              <BackToDashboard backClicked={() => navigate('/')} />
            </Grid>
            <Grid
              item
              xs={5}
              md={9}
              lg={7}
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'center',
                display: 'flex',
              }}>
              {isCancelOrderButtonVisible() && (
                <CancelOrder
                  cancelOrder={() => setShowCancelConfirmation(true)}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid
        container
        spacing={2}
        justifyContent='center'
        style={{
          marginBottom: 40,
          paddingLeft: accordionPadding,
          paddingRight: accordionPadding,
        }}>
        <Grid
          className={isOrderInactive(order?.Status) ? cancelStyle : ''}
          item
          xs={12}
          sm={12}
          md={12}
          lg={10}>
          <OrderDetailsSection
            title={isThirdPartyOrder ? 'Lender Team' : 'Escrow Team'}>
            {order && order?.OrderDetails && order.OrderDetails?.EscrowTeam && (
              <OrderDetailsTable
                columns={escrowLenderColumns}
                data={order?.OrderDetails?.EscrowTeam}
                noDataText={
                  isThirdPartyOrder
                    ? 'Lender Team not selected'
                    : 'Escrow Team not selected'
                }
              />
            )}
          </OrderDetailsSection>
        </Grid>
        <Grid
          className={isOrderInactive(order?.Status) ? cancelStyle : ''}
          item
          xs={12}
          sm={12}
          md={12}
          lg={10}>
          <OrderDetailsSection title='Signers'>
            {order && order?.OrderDetails && order.OrderDetails?.Signers && (
              <OrderDetailsTable
                columns={signerColumns}
                data={order?.OrderDetails?.Signers}
              />
            )}
          </OrderDetailsSection>
        </Grid>
        {isThirdPartyOrder && (
          <Grid
            className={isOrderInactive(order?.Status) ? cancelStyle : ''}
            item
            xs={12}
            sm={12}
            md={12}
            lg={10}>
            <OrderDetailsSection title='Settlement Team'>
              {order &&
                order?.OrderDetails &&
                order.OrderDetails?.SettlementTeam && (
                  <OrderDetailsTable
                    columns={settlementTeamColumns}
                    data={order?.OrderDetails?.SettlementTeam}
                    noDataText='Settlement Team not selected'
                  />
                )}
            </OrderDetailsSection>
          </Grid>
        )}
        <Grid item xs={12} sm={12} md={12} lg={10}>
          <DocumentsDownloader
            allSigned={
              order?.Status === OrderStatus.OpenAllSigned ||
              order?.Status === OrderStatus.ArchivedAllSigned
            }
            orderId={params.id as string}
            originalDocuments={order?.OrderDetails?.Documents}
            enableRON={order?.OrderType === OrderTypes.RON}
          />
        </Grid>
      </Grid>
    </div>
  );
}
